import React, { ComponentProps, FC, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import useClassNames from '@/helpers/hooks/useClassNames';
import Typography from '../typography';

interface DropdownOptionProps {
  label: string;
  value: string;
  iconSrc?: string;
  isSelected: boolean;
}

const DropdownOption: FC<DropdownOptionProps> = ({ label, value, iconSrc, isSelected }) => (
  <option className="text-sm lg:text-xs 2xl:text-sm font-semibold leading-3 text-[#333] w-full md:w-[35%] lg:w-[25%] xl:w-[25%] 2xl:w-[25%]" key={value} value={value} selected={isSelected}>
    {iconSrc && <img src={iconSrc} alt="Option Icon" />} {label}
  </option>
);

export interface DropdownProps extends ComponentProps<'select'> {
  className?: string;
  containerClassName?: string;
  labelClassName?: string;
  items: Array<{ label: string; value: string; iconSrc?: string }>;
  label?: string;
  value?: string;
  defaultValue?: string;
  selectOptions?: any;
  iconSrc?: string;
}

const DefaultDropdown: FC<DropdownProps> = ({
  className = '',
  containerClassName = '',
  labelClassName,
  label,
  items,
  onChange,
  value,
  selectOptions,
  iconSrc,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const containerClassNames = useClassNames(['grid w-full', containerClassName]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={containerClassNames}>
      {label && (
        <div className="flex items-center">
          <Typography as="label" className={labelClassName}>
            {`${label}${props.required ? ' *' : ''}`}
          </Typography>
        </div>
      )}

      <div className="relative h-10 md:w-[35%] 2xl:w-[25%] xl:w-[25%] lg:w-[25%] w-full overflow-hidden rounded-lg border border-neutral-500">
        <div onClick={toggleDropdown} className="flex items-center rounded-lg justify-between h-full cursor-pointer px-2">
          <select
            className={`absolute font-body z-[1] text-sm lg:text-xs 2xl:text-sm font-semibold leading-3 text-[#333] h-10 border-none focus:ring-0  bg-transparent bg-none font-regular leading-loose pl-3 pr-2 py-0 ${className}`}
            value={value}
            onChange={onChange}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown(); // Close dropdown when clicking on select
            }}
            {...props}
          >
            {items.map(({ label, value, iconSrc }) => (
              <DropdownOption
                key={value}
                label={label}
                value={value}
                iconSrc={iconSrc}
                isSelected={value === value} 
              />
            ))}
          </select>
          <ChevronDownIcon
            className={`absolute right-5 top-1/2 z-0 h-5 w-7.5 -translate-y-1/2 stroke-1 text-secondary-black transition-transform ${
              isOpen ? '' : 'rotate-180'
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export default DefaultDropdown;
